/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ConsoleLogger, LogLevel, DefaultDeviceController } from 'amazon-chime-sdk-js';
import {
  clientStartSessionNewEndPoint,
  leaveSessionNewEndPoint,
  therapistAcknowledgeSession,
  therapistStartSessionNewEndPoint
} from 'src/api/therapySession';
import { getChimeJoinData } from 'src/redux/services/teleTherapyServices';
import AppButton from 'src/components/base/Button/Button';
import Storage from 'src/latest/src/utils/Storage';
import {
  clearSession,
  setChimeSessionData,
  setJoinData,
  setMic,
  setVideo,
} from 'src/redux/actions/teleTherapyActions';
import SettingsModal2 from '../settingsModal/SettingsModal2';
import SelfVideoView from '../settingsModal/SelfVideoView';
import IconsTray from '../components/icons-tray/IconsTray';
import './waiting-room.scss'; // Import your SCSS file
import { UserRoles, UserTypes } from 'src/latest/src/constants';
import { Typography } from "src/latest/src/components";import PermissionModal from '../components/PermissionModal';
import { DevicePermission } from 'src/legacy/utils';

const WaitingRoom: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [videoFeedEnabled, setVideoFeedEnabled] = useState(true);
  const [audioFeedEnabled, setAudioFeedEnabled] = useState(true);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const teleTherapy = useSelector((state: any) => state.teleTherapy);
  const { attendees } = useSelector(({ teleTherapy }: any) => teleTherapy);
  const therapistDetails = attendees.find(
    (attendee: any) => attendee.isTherapist,
  );
  const { loggedInUser } = useSelector(
    ({ authentication }: any) => authentication,
  );
  const { joinData } = useSelector(({ teleTherapy }: any) => teleTherapy);
  const { chime_data } = useSelector(({ teleTherapy }: any) => teleTherapy);
  const [showModal, setShowModal] = useState(false);

  const checkPermissions = async () => {
    try {
      const deviceStatus = await DevicePermission();

      // Check if permissions are missing
      if (!deviceStatus.isMicrophoneAlreadyCaptured || !deviceStatus.isWebcamAlreadyCaptured) {
        setShowPermissionModal(true);

        return false;
      }

      // Check if devices are actually available
      const deviceController = new DefaultDeviceController(
        new ConsoleLogger('DeviceController', LogLevel.OFF)
      );

      const videoInputs = await deviceController.listVideoInputDevices();
      const audioInputs = await deviceController.listAudioInputDevices();

      deviceController.destroy();

      if (videoInputs.length === 0 || audioInputs.length === 0) {
        setShowPermissionModal(true);

        return false;
      }

      return true;
    } catch (error) {
      console.error('Error checking permissions:', error);
      setShowPermissionModal(true);

      return false;
    }
  };

  const saveCurrentState = () => {
    // Save current video and audio states before reload
    try {
      Storage.setItem('meetingVideoEnabled', JSON.stringify(videoFeedEnabled));
      Storage.setItem('meetingAudioEnabled', JSON.stringify(audioFeedEnabled));
      Storage.setItem('waitingRoomStatePreserved', 'true');
    } catch (error) {
      console.error('Error saving state:', error);
    }
  };

  const handlePermissionGranted = () => {
    // Save current states before reload
    saveCurrentState();
    window.location.reload();
  };

  // Permission check effect
  useEffect(() => {
    let cleanup: (() => void) | undefined;

    checkPermissions().then(hasPermissions => {
      if (hasPermissions && !loggedInUser.user_type.includes('THERAPIST')) {
        const intervalId = setInterval(async () => {
          try {
            const data = await getChimeJoinData(
              teleTherapy.teleTherapySessionData.id,
            );

            dispatch(setChimeSessionData(data, false));
          } catch (error) {
            console.error('Error fetching chime data:', error);
          }
        }, 30000);

        cleanup = () => {
          clearInterval(intervalId);
        };
      }
    });

    return () => {
      cleanup?.();
    };
  }, []);

  // Initialize state from storage when component mounts
  useEffect(() => {
    try {
      const savedVideoState = Storage.getItem('meetingVideoEnabled');
      const savedAudioState = Storage.getItem('meetingAudioEnabled');
      const statePreserved = Storage.getItem('waitingRoomStatePreserved');

      if (savedVideoState !== null) {
        const videoState = JSON.parse(savedVideoState);

        setVideoFeedEnabled(videoState);
        dispatch(setVideo(videoState));
      }

      if (savedAudioState !== null) {
        const audioState = JSON.parse(savedAudioState);

        setAudioFeedEnabled(audioState);
        dispatch(setMic(audioState));
      }

      // Clear the preservation flag after restoring state
      if (statePreserved) {
        Storage.removeItem('waitingRoomStatePreserved');
      }
    } catch (error) {
      console.error('Error restoring state:', error);
    }
  }, []);

  // Save state before toggling to ensure it persists through permission checks
  const toggleVideoFeed = async () => {
    const hasPermissions = await checkPermissions();

    if (!hasPermissions) {
      saveCurrentState();

      return;
    }

    const newState = !videoFeedEnabled;

    setVideoFeedEnabled(newState);
    dispatch(setVideo(newState));

    // Save new state immediately
    Storage.setItem('meetingVideoEnabled', JSON.stringify(newState));
  };

  const toggleAudioFeed = async () => {
    const hasPermissions = await checkPermissions();

    if (!hasPermissions) {
      saveCurrentState();

      return;
    }

    const newState = !audioFeedEnabled;

    setAudioFeedEnabled(newState);
    dispatch(setMic(newState));

    // Save new state immediately
    Storage.setItem('meetingAudioEnabled', JSON.stringify(newState));
  };

  const leaveWatingRoom = async () => {
    // Clear preserved states when explicitly leaving
    Storage.removeItem('meetingVideoEnabled');
    Storage.removeItem('meetingAudioEnabled');
    Storage.removeItem('waitingRoomStatePreserved');

    if (loggedInUser.user_type.includes('CLIENT')) {

      if(teleTherapy.teleTherapySessionData.id) {
      await leaveSessionNewEndPoint(teleTherapy.teleTherapySessionData.id);
      }

      dispatch(clearSession());
      navigate('/');
    } else if (loggedInUser.user_type.includes('THERAPIST')) {
      dispatch(clearSession());
      navigate('/');
    }
  };

  // Add permission check before joining meeting
  const handleJoinMeeting = async () => {
    const hasPermissions = await checkPermissions();

    if (!hasPermissions) {
      return;
    }

    try {
      if (loggedInUser.user_type.includes('CLIENT')) {
        await clientStartSessionNewEndPoint(
          teleTherapy.teleTherapySessionData.id,
        );
      } else if (loggedInUser.user_type.includes('THERAPIST')) {
           const clientId = attendees.filter(
             (attendee: any) => !attendee.isTherapist)[0].id;

           const therapistChimeData = await therapistStartSessionNewEndPoint(
          clientId
        ); // start the session by therapist

        await dispatch(
            setJoinData({ joinData: therapistChimeData, isReadyToJoin: false })
        );

        if (clientId) {
          await therapistAcknowledgeSession(clientId);
          Storage.removeItem("pendingClientId");
        }
      }

      Storage.setItem('meetingInitiatedFromWaitingRoom', 'true');

      navigate('/teletherapy/meeting-room');

    } catch (error) {
      console.error('Error joining meeting:', error);
    }
  };

  return (
    <div className="waiting-room-container">
      <PermissionModal
        show={showPermissionModal}
        onClose={() => setShowPermissionModal(false)}
        onPermissionGranted={handlePermissionGranted}
      />
      <div className="waiting-room-header">
        <Typography
          variant="size-34"
          color="color-2"
          className="waiting-room-header__text"
        >
          {loggedInUser.user_type.includes('THERAPIST')
            ? `You are about to start a session with ${
                attendees.filter((a: any) => a.id !== loggedInUser.id)[0]
                  ?.fullName
              }`
            : `You are in ${therapistDetails?.fullName}'s waiting room`}
        </Typography>
      </div>
      <div className="info-card d-flex">
       {loggedInUser.user_type.includes(UserTypes.CLIENT) && (
          <p className={'info-card__text'}>
            Your therapist will only be available for scheduled sessions. Make
            sure you have already made an appointment. You'll be able to join the
            meeting room when your therapist is ready.
          </p>
        )}
        {loggedInUser.user_type.includes(UserRoles.THERAPIST) && (
          <p className={'info-card__text text-therapist'}>
            Are you ready ?
          </p>
        )}
        <AppButton
          variant="primary"
          className="join-button"
          onClick={handleJoinMeeting}
          disabled={
            loggedInUser.user_type.includes('CLIENT')
              ? !chime_data || Object.entries(chime_data).length === 0
              : !joinData
          }
          label={'Join Now →'}
        />
      </div>

      <div className="video-container">
        <SelfVideoView />
      </div>

      <div className="d-flex">
        <IconsTray
          isVideoEnabled={videoFeedEnabled}
          toggleVideo={toggleVideoFeed}
          muted={!audioFeedEnabled}
          toggleAudioMute={toggleAudioFeed}
          setShowModal={setShowModal}
          leaveMeeting={leaveWatingRoom}
          isTherapist={loggedInUser.user_type.includes('THERAPIST')}
        />
      </div>
      <button className="leave-button" onClick={leaveWatingRoom}>
        Leave Waiting Room
      </button>

      <SettingsModal2 showModal={showModal} setShowModal={setShowModal} />
    </div>
  );
};

export default WaitingRoom;
